import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Mushroom } from './Mushroom';
import { MushroomField } from '../systems/MushroomField';

export interface CentipedeSegment {
  position: Vector2;
  isHead: boolean;
  active: boolean;
}

export class Centipede implements GameObject {
  public id: string;
  public position: Vector2; // Head position
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private segments: CentipedeSegment[] = [];
  private speed: number;
  private direction: number = 1; // 1 for right, -1 for left
  private gridSize: number = 16;
  private gameWidth: number;
  private gameHeight: number;
  private segmentSpacing: number = 16;
  
  // Movement state
  private isDescending: boolean = false;
  private descendDistance: number = 0;
  private targetDescendDistance: number = 16;
  private mushrooms: Mushroom[] = [];
  private isCrazy: boolean = false; // When hit poison mushroom
  private crazySpeed: number = 200; // Fast plunge speed
  
  // Animation
  private animationTime: number = 0;
  private animationFrame: number = 0;

  constructor(
    id: string,
    x: number,
    y: number,
    segmentCount: number,
    speed: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number,
    gameHeight: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: speed, y: 0 };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.speed = speed;
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;
    
    // Create segments
    this.createSegments(segmentCount);
  }

  private createSegments(count: number): void {
    this.segments = [];
    
    for (let i = 0; i < count; i++) {
      const segment: CentipedeSegment = {
        position: {
          x: this.position.x - (i * this.segmentSpacing),
          y: this.position.y
        },
        isHead: i === 0,
        active: true
      };
      
      this.segments.push(segment);
    }
  }

  public setMushrooms(mushrooms: Mushroom[]): void {
    this.mushrooms = mushrooms;
  }

  public update(deltaTime: number): void {
    // Update animation
    this.animationTime += deltaTime;
    if (this.animationTime >= 0.2) {
      this.animationTime = 0;
      this.animationFrame = (this.animationFrame + 1) % 4;
    }

    if (this.isCrazy) {
      this.updateCrazyMovement(deltaTime);
    } else if (this.isDescending) {
      this.updateDescending(deltaTime);
    } else {
      this.updateHorizontalMovement(deltaTime);
    }
    
    // Update segments to follow
    this.updateSegments();
    
    // Update head position to first active segment
    const head = this.segments.find(segment => segment.active);
    if (head) {
      this.position = { ...head.position };
    }
    
    // Check if all segments are destroyed
    const activeSegments = this.segments.filter(s => s.active);
    if (activeSegments.length === 0) {
      this.active = false;
    }
  }

  private updateHorizontalMovement(deltaTime: number): void {
    // Find the first active segment to be the head
    const head = this.segments.find(segment => segment.active);
    if (!head) return; // No active segments left
    
    const nextX = head.position.x + (this.speed * this.direction * deltaTime);
    
    // Check boundaries and mushroom collisions
    const hitBoundary = (this.direction > 0 && nextX >= this.gameWidth - this.size.x) ||
                       (this.direction < 0 && nextX <= 0);
    
    const hitMushroom = this.checkMushroomCollision(nextX, head.position.y);
    
    if (hitBoundary || hitMushroom) {
      // Start descending and change direction
      this.isDescending = true;
      this.descendDistance = 0;
      this.direction *= -1;
    } else {
      // Continue horizontal movement
      head.position.x = nextX;
    }
  }

  private updateDescending(deltaTime: number): void {
    const head = this.segments.find(segment => segment.active);
    if (!head) return; // No active segments left
    
    const moveAmount = this.speed * deltaTime;
    
    head.position.y += moveAmount;
    this.descendDistance += moveAmount;
    
    if (this.descendDistance >= this.targetDescendDistance) {
      this.isDescending = false;
      this.descendDistance = 0;
      
      // Snap to grid
      head.position.y = Math.round(head.position.y / this.gridSize) * this.gridSize;
    }
  }

  private updateSegments(): void {
    // Each segment follows the one in front of it
    for (let i = 1; i < this.segments.length; i++) {
      if (!this.segments[i].active) continue;
      
      const current = this.segments[i];
      const leader = this.segments[i - 1];
      
      if (!leader.active) {
        // Find the next active segment to follow
        let nextLeader = null;
        for (let j = i - 1; j >= 0; j--) {
          if (this.segments[j].active) {
            nextLeader = this.segments[j];
            break;
          }
        }
        
        if (!nextLeader) continue;
        
        const dx = nextLeader.position.x - current.position.x;
        const dy = nextLeader.position.y - current.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > this.segmentSpacing) {
          const moveRatio = (distance - this.segmentSpacing) / distance;
          current.position.x += dx * moveRatio;
          current.position.y += dy * moveRatio;
        }
      } else {
        // Normal following behavior
        const dx = leader.position.x - current.position.x;
        const dy = leader.position.y - current.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > this.segmentSpacing) {
          const moveRatio = (distance - this.segmentSpacing) / distance;
          current.position.x += dx * moveRatio;
          current.position.y += dy * moveRatio;
        }
      }
    }
  }

  private checkMushroomCollision(x: number, y: number): boolean {
    const testBounds = {
      x: x,
      y: y,
      width: this.size.x,
      height: this.size.y
    };
    
    for (const mushroom of this.mushrooms) {
      if (!mushroom.active) continue;
      
      const mushroomBounds = mushroom.getBounds();
      const collision = testBounds.x < mushroomBounds.x + mushroomBounds.width &&
                       testBounds.x + testBounds.width > mushroomBounds.x &&
                       testBounds.y < mushroomBounds.y + mushroomBounds.height &&
                       testBounds.y + testBounds.height > mushroomBounds.y;
      
      if (collision) {
        // Check if it's a poison mushroom
        if (mushroom.isPoisonMushroom()) {
          this.isCrazy = true; // Go crazy and plunge down!
        }
        return true;
      }
    }
    
    return false;
  }

  private updateCrazyMovement(deltaTime: number): void {
    const head = this.segments.find(segment => segment.active);
    if (!head) return; // No active segments left
    
    // Plunge straight down at high speed
    head.position.y += this.crazySpeed * deltaTime;
    
    // Remove when off bottom of screen
    if (head.position.y > this.gameHeight) {
      this.active = false;
    }
  }

  public checkBulletCollision(bulletBounds: Rectangle): number {
    // Check collision with each segment
    for (let i = 0; i < this.segments.length; i++) {
      const segment = this.segments[i];
      if (!segment.active) continue;
      
      const segmentBounds = {
        x: segment.position.x,
        y: segment.position.y,
        width: this.size.x,
        height: this.size.y
      };
      
      if (bulletBounds.x < segmentBounds.x + segmentBounds.width &&
          bulletBounds.x + bulletBounds.width > segmentBounds.x &&
          bulletBounds.y < segmentBounds.y + segmentBounds.height &&
          bulletBounds.y + bulletBounds.height > segmentBounds.y) {
        return i; // Return segment index that was hit
      }
    }
    
    return -1; // No collision
  }

  public checkPlayerCollision(playerBounds: Rectangle): boolean {
    return this.segments.some(segment => {
      if (!segment.active) return false;
      
      const segmentBounds = {
        x: segment.position.x,
        y: segment.position.y,
        width: this.size.x,
        height: this.size.y
      };
      
      return playerBounds.x < segmentBounds.x + segmentBounds.width &&
             playerBounds.x + playerBounds.width > segmentBounds.x &&
             playerBounds.y < segmentBounds.y + segmentBounds.height &&
             playerBounds.y + playerBounds.height > segmentBounds.y;
    });
  }

  public hitSegment(segmentIndex: number, mushroomField: MushroomField): number {
    if (segmentIndex < 0 || segmentIndex >= this.segments.length) return 0;
    
    const segment = this.segments[segmentIndex];
    if (!segment.active) return 0;
    
    // Create mushroom at segment position
    mushroomField.addMushroom(segment.position.x, segment.position.y);
    
    // Deactivate the segment
    segment.active = false;
    
    // Calculate points based on official manual values
    let points = segment.isHead ? 100 : 10; // Head = 100 pts, Body = 10 pts
    
    // If this split the centipede, create a new centipede from remaining segments
    this.splitCentipede(segmentIndex);
    
    return points;
  }

  private splitCentipede(hitIndex: number): void {
    // Find segments after the hit that are still active
    const remainingSegments = this.segments.slice(hitIndex + 1).filter(s => s.active);
    
    if (remainingSegments.length > 0) {
      // The first remaining segment becomes the new head
      remainingSegments[0].isHead = true;
      
      // Remove the tail segments from this centipede
      this.segments = this.segments.slice(0, hitIndex);
      
      // TODO: Create a new Centipede object for the remaining segments
      // This would require access to the game's centipede array
      // For now, we'll just mark them as part of this centipede with new head
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    this.segments.forEach((segment, index) => {
      if (!segment.active) return;
      
      this.renderSegment(segment, index);
    });
  }

  private renderSegment(segment: CentipedeSegment, index: number): void {
    const centerX = segment.position.x + this.size.x / 2;
    const centerY = segment.position.y + this.size.y / 2;
    
    let pattern: number[][];
    let color: string;
    
    if (segment.isHead) {
      // Head has different patterns based on animation frame
      switch (this.animationFrame % 2) {
        case 0:
          pattern = [
            [0,0,1,1,1,1,0,0],
            [0,1,1,0,0,1,1,0],
            [1,1,0,1,1,0,1,1],
            [1,0,1,1,1,1,0,1],
            [1,1,1,0,0,1,1,1],
            [1,1,0,1,1,0,1,1],
            [0,1,1,1,1,1,1,0],
            [0,0,1,1,1,1,0,0]
          ];
          break;
        case 1:
          pattern = [
            [0,0,1,1,1,1,0,0],
            [0,1,0,1,1,0,1,0],
            [1,0,1,1,1,1,0,1],
            [1,1,1,1,1,1,1,1],
            [1,1,1,0,0,1,1,1],
            [1,0,1,1,1,1,0,1],
            [0,1,0,1,1,0,1,0],
            [0,0,1,1,1,1,0,0]
          ];
          break;
        default:
          pattern = [
            [0,0,1,1,1,1,0,0],
            [0,1,1,1,1,1,1,0],
            [1,1,1,1,1,1,1,1],
            [1,1,1,1,1,1,1,1],
            [1,1,1,0,0,1,1,1],
            [1,1,1,1,1,1,1,1],
            [0,1,1,1,1,1,1,0],
            [0,0,1,1,1,1,0,0]
          ];
      }
      color = '#FF0000'; // Red head
    } else {
      // Body segments
      pattern = [
        [0,0,1,1,1,1,0,0],
        [0,1,1,1,1,1,1,0],
        [1,1,0,1,1,0,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,0,1,1,0,1,1],
        [0,1,1,1,1,1,1,0],
        [0,0,1,1,1,1,0,0]
      ];
      color = index % 2 === 0 ? '#00FF00' : '#FFFF00'; // Alternating green/yellow body
    }

    const pixelSize = 2;
    
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - (pattern[row].length * pixelSize) / 2 + col * pixelSize;
          const y = centerY - (pattern.length * pixelSize) / 2 + row * pixelSize;
          this.renderManager.fillRect(x, y, pixelSize, pixelSize, color);
        }
      }
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public getActiveSegmentCount(): number {
    return this.segments.filter(s => s.active).length;
  }
}